import { isSupportedMcapCanonicalPair } from '@/features/assets/lib/mcapCanonicalRepresentation';
import type {
  McapCanonicalRepresentation,
  McapObjectIdentity,
  McapProjectAsset,
  McapSourceSetUnion,
  McapVideoIndexVersion,
} from '@/features/assets/types/asset';
import { z } from 'zod';

const unsignedDecimalSchema = z.string().regex(/^\d+$/);
const lowercaseSlugSchema = z.string().regex(/^[a-z0-9-]+$/);
const sessionIdSchema = z.string().regex(/^up-[0-9a-f]{12}-\d+$/);

export const initiateMcapSubmissionInputSchema = z
  .object({
    sessionId: sessionIdSchema,
    projectId: lowercaseSlugSchema,
    contentBlake3: z.string().regex(/^[0-9a-f]{64}$/),
    declaredBytes: unsignedDecimalSchema,
    mediaType: z.literal('mcap'),
    originalFilename: z.string().refine((value) => value.trim().length > 0),
  })
  .strict();

export type InitiateMcapSubmissionInput = z.infer<typeof initiateMcapSubmissionInputSchema>;

type SubmissionStatus = 'pending' | 'uploading' | 'uploaded' | 'failed' | 'abandoned';

export interface SubmissionSession {
  sessionId: string;
  projectId: string;
  originalFilename: string;
  contentBlake3: string;
  declaredBytes: string;
  receivedBytes: string;
  nextPart: number;
  status: SubmissionStatus;
  failReason: string | null;
  resumableUri: string | null;
  mediaType: 'mcap';
}

export interface UploaderSubmissionSession extends Omit<SubmissionSession, 'mediaType'> {
  mediaType: 'video' | 'mcap';
}

export interface UploaderSubmissionPage {
  items: UploaderSubmissionSession[];
  nextCursor: string | null;
}

export interface McapIngest {
  sessionId: string;
  assetId: string;
  status: 'queued' | 'ready' | 'failed';
  failure: string | null;
  failureCode?: string | null;
  checkGeneration: number;
  taskId: string;
  recheckable: boolean;
}

export interface McapRecheckResponse {
  requestId: string;
  sessionId: string;
  oldTaskId: string;
  newTaskId: string;
  actor: string;
  reason: string;
  createdAtMs: number;
  replayed: boolean;
}

export interface McapVideoTrack {
  channelId: number;
  topic: string;
  codec: string;
  messageEncoding: string;
  messageCount: string;
  startTimeNs: string;
  endTimeNs: string;
  descriptorVersion: number;
  descriptor: unknown;
}

export interface McapMetadata {
  /** Current READY video-source revision used to associate Inco annotations. */
  observationSourceRevision: string | null;
  asset: McapProjectAsset;
  ingest: McapIngest | null;
  videoTracks: McapVideoTrack[];
}

export interface MetadataDocumentEnvelope {
  envelopeVersion: 1;
  documentVersion: 'mcap-scan-manifest-v2';
  documentBlake3: string;
  document: Record<string, unknown>;
}

export interface McapUploadStatus {
  session: SubmissionSession;
  assetId: string | null;
  ingest: McapIngest | null;
  metadata: McapMetadata | null;
}

interface McapPlaybackLease {
  url: string;
  expiresAt: string;
  mediaType: 'application/vnd.mcap';
  name: string;
}

type McapPlaybackMainMember = McapObjectIdentity &
  McapPlaybackLease & { memberRank: 0; role: 'main' };

type McapPlaybackVideoMember = McapObjectIdentity &
  McapPlaybackLease & { memberRank: number; role: 'video'; videoChannelId: number };

export type McapPlaybackSourceSet =
  | { version: 1; members: [McapPlaybackMainMember] }
  | {
      version: 2;
      manifest: McapObjectIdentity;
      members: Array<McapPlaybackMainMember | McapPlaybackVideoMember>;
      union: McapSourceSetUnion;
    };

type McapHandposeFailure =
  | 'task_failed'
  | 'task_cancelled'
  | 'malformed_terminal'
  | 'stale_source'
  | 'stale_frozen_input'
  | 'terminal_binding_mismatch'
  | 'immutable_evidence_mismatch';

type McapHandposePipelineVersion =
  | 'mcap-handpose/filtered-21-v4'
  | 'mcap-handpose/filtered-21-v4.1';

export interface McapHandposeReadyArtifact {
  artifactUri: string;
  gcsGeneration: string;
  artifactBlake3: string;
  artifactSizeBytes: string;
  pipelineVersion: McapHandposePipelineVersion;
  modelSha256: string;
  topicContractVersion: 4 | 5;
  startTimestampNs: string;
  endTimestampNs: string;
  frameCount: string;
}

export interface McapHandposeFeature {
  assetId: string;
  state: 'queued' | 'ready' | 'failed';
  taskId: string;
  pipelineVersion: McapHandposePipelineVersion;
  modelSha256: string;
  sourceGcsUri: string;
  sourceGcsGeneration: string;
  sourceBlake3: string;
  sourceSizeBytes: string;
  failureCode: McapHandposeFailure | null;
  failureReason?: string | null;
  ready: McapHandposeReadyArtifact | null;
}

export type McapHandposeStatus = McapHandposeFeature | { assetId: string; state: 'not_started' };

export const mcapHandposeRetryRequestSchema = z
  .object({
    requestId: lowercaseSlugSchema,
    failedTaskId: z.string().trim().min(1),
    reason: z.string().trim().min(1),
  })
  .strict();

export type McapHandposeRetryRequest = z.infer<typeof mcapHandposeRetryRequestSchema>;

export interface McapHandposeRetryResponse {
  requestId: string;
  assetId: string;
  oldTaskId: string;
  newTaskId: string;
  actor: string;
  reason: string;
  createdAtMs: number;
  replayed: boolean;
  feature: McapHandposeFeature;
}

export interface McapHandposePlaybackLease {
  url: string;
  expiresAt: string;
  sizeBytes: string;
  blake3: string;
  mediaType: 'application/vnd.mcap';
  name: 'handpose.mcap';
}

type McapHandposeAnalysisModeKind = 'motion-mode' | 'hand-absent' | 'quality-degraded';

export interface McapHandposeAnalysisNode {
  id: string;
  start: number;
  end: number;
  kind: McapHandposeAnalysisModeKind;
  mode: number;
  color: string;
  mergeCost: number;
  children: McapHandposeAnalysisNode[];
}

export interface McapHandposeObservationOverlay {
  start: number;
  end: number;
  kind: 'hand-absent' | 'quality-degraded';
}

export interface McapHandposeAnalysisResult {
  originTimestampNs: string;
  sampleCount: number;
  modeTree: McapHandposeAnalysisNode;
  hierarchyCosts: number[];
  observationOverlays: McapHandposeObservationOverlay[];
  processedMessageCount: number;
  skippedMessageCount: number;
}

/** Runtime validation is retained because this is an untyped HTTP error payload. */
export const mcapHandposeAnalysisReadinessErrorSchema = z
  .object({ error: z.enum(['handpose_not_ready', 'handpose_failed']) })
  .strict();

export type McapHandposeAnalysisReadinessError = z.infer<
  typeof mcapHandposeAnalysisReadinessErrorSchema
>;

export const segmentPreviewInputSchema = z
  .object({
    logicalStartNs: unsignedDecimalSchema,
    logicalEndNs: unsignedDecimalSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (BigInt(value.logicalStartNs) >= BigInt(value.logicalEndNs)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'start_must_precede_end' });
    }
  });

export type SegmentPreviewInput = z.infer<typeof segmentPreviewInputSchema>;

interface SegmentVideoTrackPlan {
  channelId: number;
  idrOrdinal: string;
  idrLogTimeNs: string;
  copyStartNs: string;
  presentationStartNs: string;
  prerollNs: string;
}

export interface SegmentPlan extends McapCanonicalRepresentation {
  version: 2;
  sourceAssetId: string;
  canonicalGcsGeneration: string;
  logicalStartNs: string;
  logicalEndNs: string;
  indexVersion: McapVideoIndexVersion;
  indexBlake3: string;
  videoTracks: SegmentVideoTrackPlan[];
  nonVideoPolicy: 'copy-logical-window';
  snapshotPolicy: {
    mode: 'latest-at-or-before-start';
    channels: Array<{ channelId: number; topic: string; firstLogTimeNs: string }>;
  };
  attachmentPolicy: 'logical-window';
  metadataPolicy: 'copy-all';
}

const mcapChannelIdInputSchema = z.number().int().min(0).max(65_535);
const segmentVideoTrackPlanInputSchema = z
  .object({
    channelId: mcapChannelIdInputSchema,
    idrOrdinal: unsignedDecimalSchema,
    idrLogTimeNs: unsignedDecimalSchema,
    copyStartNs: unsignedDecimalSchema,
    presentationStartNs: unsignedDecimalSchema,
    prerollNs: unsignedDecimalSchema,
  })
  .strict();

const segmentPlanInputSchema = z
  .object({
    version: z.literal(2),
    sourceAssetId: z.string(),
    canonicalObject: z.string(),
    canonicalBlake3: z.string().regex(/^[0-9a-f]{64}$/),
    canonicalFileSizeBytes: unsignedDecimalSchema,
    canonicalGcsGeneration: unsignedDecimalSchema,
    normalizerVersion: z.string(),
    layoutProfile: z.enum(['time-clustered-v1', 'source-set-v2']),
    logicalStartNs: unsignedDecimalSchema,
    logicalEndNs: unsignedDecimalSchema,
    indexVersion: z.literal('mcap-video-index/v2'),
    indexBlake3: z.string().regex(/^[0-9a-f]{64}$/),
    videoTracks: z.array(segmentVideoTrackPlanInputSchema).min(1),
    nonVideoPolicy: z.literal('copy-logical-window'),
    snapshotPolicy: z
      .object({
        mode: z.literal('latest-at-or-before-start'),
        channels: z.array(
          z
            .object({
              channelId: mcapChannelIdInputSchema,
              topic: z.string(),
              firstLogTimeNs: unsignedDecimalSchema,
            })
            .strict(),
        ),
      })
      .strict(),
    attachmentPolicy: z.literal('logical-window'),
    metadataPolicy: z.literal('copy-all'),
  })
  .strict()
  .superRefine((plan, context) => {
    if (!isSupportedMcapCanonicalPair(plan.normalizerVersion, plan.layoutProfile)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'normalizer_version_and_layout_profile_mismatch',
      });
    }
    if (BigInt(plan.logicalEndNs) <= BigInt(plan.logicalStartNs)) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'invalid_segment_range' });
    }
    const channelIds = new Set(plan.videoTracks.map((track) => track.channelId));
    if (channelIds.size !== plan.videoTracks.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'duplicate_segment_video_track_channel_id',
        path: ['videoTracks'],
      });
    }
    for (const track of plan.videoTracks) {
      const copyStart = BigInt(track.copyStartNs);
      const presentationStart = BigInt(track.presentationStartNs);
      if (
        track.presentationStartNs !== plan.logicalStartNs ||
        track.idrLogTimeNs !== track.copyStartNs ||
        copyStart > presentationStart ||
        presentationStart - copyStart !== BigInt(track.prerollNs) ||
        BigInt(track.prerollNs) > 5_000_000_000n
      ) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'invalid_track_preroll' });
      }
    }
  });

export const createSegmentInputSchema = z
  .object({
    segmentId: lowercaseSlugSchema,
    expectedPlan: segmentPlanInputSchema,
  })
  .strict();

export type CreateSegmentInput = z.infer<typeof createSegmentInputSchema>;

interface ClipCreator {
  displayName: string | null;
  email: string | null;
  photoUrl: string | null;
}

export interface LogicalSegmentSummary {
  segmentId: string;
  key?: string | null;
  assetId: string;
  createdBy: string | null;
  creator: ClipCreator | null;
  createdAt: string;
  logicalStartNs: string;
  logicalEndNs: string;
}

export type LogicalSegment = Omit<LogicalSegmentSummary, 'key'> &
  Omit<McapCanonicalRepresentation, 'canonicalGcsGeneration'> & {
    key: string;
    indexVersion: McapVideoIndexVersion;
    indexBlake3: string;
    plan: SegmentPlan;
  };

type LogicalSegmentList = LogicalSegment[];

/**
 * The asset-local endpoint's canonical response is a bare array. The legacy `{ items }` envelope
 * remains part of the coordinated wire contract during rollout and is normalized by the query
 * observer rather than by the API transport.
 */
export type LogicalSegmentListResponse = LogicalSegmentList | { items: LogicalSegmentList };

export interface LogicalSegmentPage {
  items: LogicalSegmentSummary[];
  nextCursor: string | null;
}

interface DeliveryConfig {
  schemaVersion: 2;
  kind: 'mcap';
  includeHandpose: boolean;
}

const deliveryConfigInputSchema = z
  .object({
    schemaVersion: z.literal(2),
    kind: z.literal('mcap'),
    includeHandpose: z.boolean(),
  })
  .strict();

export const createDeliveryInputSchema = z
  .object({
    deliveryId: lowercaseSlugSchema,
    requiresVideoReencode: z.literal(false),
    config: deliveryConfigInputSchema,
  })
  .strict();

export type CreateDeliveryInput = z.infer<typeof createDeliveryInputSchema>;

export interface DeliveryResult {
  artifactBlake3: string;
  artifactSizeBytes: string;
}

export interface DeliveryTask {
  deliveryId: string;
  segmentId: string;
  taskId: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  requiresVideoReencode: false;
  config: DeliveryConfig;
  reconciled: boolean;
  errorCode: 'delivery_failed' | 'delivery_cancelled' | null;
  result: DeliveryResult | null;
  error: string;
  errorOutput: string;
}

export interface DeliveryTaskPage {
  items: DeliveryTask[];
  nextCursor: string | null;
}

export interface DeliveryDownloadLease {
  url: string;
  expiresAt: string;
  sizeBytes: string;
  mediaType: 'application/vnd.mcap';
  name: string;
  artifactBlake3: string;
}
